import {
  collection,
  addDoc,
  setDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc, query, orderBy
} from 'firebase/firestore';
import { deleteObject, ref, uploadBytes } from 'firebase/storage';

import { db, storage } from 'configuration/firebase';
import { Roles } from 'constants/enums';
import type { Client } from 'models/client.interface';
import type { Document } from 'models/document.interface';
import type { CreateClientFormData } from 'utils/validations/CreateClientSchema';

export const createClient = async (clientData: CreateClientFormData, loggedUserId: string): Promise<Client> => {
  try {
    const clientDoc = {
      role: Roles.user,
      clientName: clientData.clientName,
      userId: clientData?.userId,
      createdAt: new Date().getTime(),
      createdBy: loggedUserId,
    };
    
    const docRef = await addDoc(collection(db, 'clients'), clientDoc);
    
    return {
      id: docRef.id,
      ...clientDoc,
    } as Client;
  } catch (error) {
    console.error('Error creating client:', error);
    throw error;
  }
};

export const getClients = async (): Promise<Client[]> => {
  try {
    const clientsQuery = query(
      collection(db, 'clients'),
      orderBy('clientName', 'asc')
    );
    const querySnapshot = await getDocs(clientsQuery);
    return querySnapshot.docs.map(doc => {
      const { clientName, role, createdAt, updatedAt, userId, createdBy, updatedBy } = doc.data();

      return {
        id: doc.id,
        clientName,
        role,
        createdAt,
        userId,
        updatedAt,
        createdBy,
        updatedBy,
      } as Client;
    });
  } catch (error) {
    console.error('Error fetching clients:', error);
    throw error;
  }
};

export const getClientDetails = async (clientId: string): Promise<Client | null> => {
  try {
    const docRef = doc(db, 'clients', clientId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const { clientName, role, createdAt, updatedAt, userId, createdBy, updatedBy } = docSnap.data();

      return {
        id: docSnap.id,
        clientName,
        role,
        createdAt,
        createdBy,
        updatedAt,
        updatedBy,
        userId,
      } as Client;
    }

    return null;
  } catch (error) {
    console.error('Error fetching client:', error);
    throw error;
  }
};

export const updateClient = async (
  clientId: string,
  updateData: { clientName: string, updatedAt: number, updatedBy: string, userId: string }
): Promise<void> => {
  try {
    const clientRef = doc(db, 'clients', clientId);
    await updateDoc(clientRef, updateData);
  } catch (error) {
    console.error('Error updating client:', error);
    throw error;
  }
};

export const deleteClient = async (clientId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'clients', clientId));
  } catch (error) {
    console.error('Error deleting client:', error);
    throw error;
  }
}; 

// export const uploadClientDocument = async (clientId: string, file: File, loggedUserId: string): Promise<void> => {
//   try {
//     const fileDocRef = doc(collection(db, `clients/${clientId}/files`));
//     const storageRef = ref(storage, `clients/${clientId}/${fileDocRef.id}`);
//     const uploadTask = await uploadBytes(storageRef, file);
//     await setDoc(fileDocRef, {
//       name: file.name,
//       size: file.size,
//       contentType: file.type,
//       path: uploadTask.ref.fullPath,
//       uploadedAt: new Date().getTime(),
//       uploadedBy: loggedUserId,
//     } as Document);
//   } catch (error) {
//     console.error('Error uploading document:', error);
//     throw error;
//   }
// };

export const uploadClientDocument = async (
  clientId: string,
  files: File[],
  loggedUserId: string
): Promise<void> => {
  try {
    const filesArray = Array.isArray(files) ? files : [files];

    await Promise.all(
      filesArray.map(async (file) => {
        const fileDocRef = doc(collection(db, `clients/${clientId}/files`));
        const storageRef = ref(storage, `clients/${clientId}/${fileDocRef.id}`);

        const uploadTask = await uploadBytes(storageRef, file);

        await setDoc(fileDocRef, {
          name: file.name,
          size: file.size,
          contentType: file.type,
          path: uploadTask.ref.fullPath,
          uploadedAt: new Date().getTime(),
          uploadedBy: loggedUserId,
        } as Document);
      })
    );
  } catch (error) {
    console.error('Error uploading document(s):', error);
    throw error;
  }
};

export const getClientDocuments = async (clientId: string): Promise<Document[]> => {
  try {
    const filesQuery = query(
      collection(db, `clients/${clientId}/files`),
      orderBy('uploadedAt', 'desc')
    );
    const docsSnap = await getDocs(filesQuery);

    const documents: Document[] = docsSnap.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name,
        size: data.size,
        contentType: data.contentType,
        path: data.path,
        uploadedAt: data.uploadedAt,
        uploadedBy: data.uploadedBy,
        url: data.url,
      };
    });

    return documents;
  } catch (error) {
    console.error('Error fetching documents:', error);
    throw error;
  }
};

export const deleteDocument = async (documentId: string, clientId: string): Promise<void> => {
  try {
    // Delete from Firebase Storage
    const storageRef = ref(storage, `clients/${clientId}/${documentId}`);
    await deleteObject(storageRef);

    // Delete from Firestore
    await deleteDoc(doc(db, `clients/${clientId}/files/${documentId}`));
  } catch (error) {
    console.error('Error deleting client:', error);
    throw error;
  }
};
